import classNames from "classnames";
import React, { type JSX } from "react";
import { connect } from "react-redux";
import { t } from "ttag";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";

import { ProductOptionSelectorSet } from "../../../common/ProductOptionSelectorSet";
import { ConfiguratorTypes } from "../../../constants";
import {
    IOptionCode,
    IOptionSingleValue,
    IOptionValues,
    IProduct,
    IVerboseProductClass,
} from "../../../models/catalogue.interfaces";
import { IWebPageURL } from "../../../models/nominals";
import { IRootState, TDispatchMapper } from "../../reducers.interfaces";
import { defaults } from "../defaults";
import { Dispatchers } from "../dispatchers";
import { IModularConfiguratorOptionSet } from "../models.interfaces";
import {
    baseVariantSelector,
    optionCodesSelector,
    rootProductSelector,
} from "../selectors";
import { QuantitySelector } from "./QuantitySelector";
import { RootProductSelector } from "./RootProductSelector";

interface IOwnProps {
    optionSet: IModularConfiguratorOptionSet;
    disableQuantity?: boolean;
    style?: "full" | "mini";
    chatLinkComponent?: JSX.Element | null;
    liveChatHeader?: string;
    financingLink?: IWebPageURL;
    isPlpPanelStyle?: boolean;
}

interface IReduxProps {
    rootProduct: IProduct | null;
    productClass: IVerboseProductClass | null;
    variant: IProduct | null;
    optionCodes: IOptionCode[];
    optionValues: IOptionValues;
    panelTriggerClicked: boolean;
}

interface IDispatchProps {
    dispatchers: Dispatchers;
    onOptionChange: (
        namespace: string,
        code: IOptionCode,
        index: number,
        totalNumValues: number,
        value: IOptionSingleValue,
    ) => void;
}

interface IProps extends IOwnProps, IReduxProps, IDispatchProps {}

interface IState {}

// TODO(ssr, LEVEL 7 out of 10): Migrate this component from class-style to functional-style. Rated LOE as 7/10 due to 213 LOC, componentDidUpdate lifecycle with complex option change detection logic, Redux integration across configurator slice, error message state management with internationalization, quantity selector conditional rendering, and root product selector coordination.
class ConfiguratorOptionsComponent extends React.PureComponent<IProps, IState> {
    static defaultProps: Partial<IProps> = {
        disableQuantity: false,
        style: "full",
    };

    componentDidUpdate(prevProps: IReduxProps) {
        if (this.props.optionSet.layout.design !== ConfiguratorTypes.PANEL) {
            return;
        }
        if (!this.props.panelTriggerClicked) {
            return;
        }
        if (!this.props.rootProduct || !prevProps.rootProduct) {
            return;
        }
        const optionCodes =
            this.props.rootProduct.attributes.product_options?.value || [];
        const preOptionCodes =
            prevProps.rootProduct.attributes.product_options?.value || [];
        const optionValues = optionCodes.map((code) => {
            return {
                code: code,
                value: this.props.optionValues[code] || "",
            };
        });
        const preOptionValues = preOptionCodes.map((code) => {
            return {
                code: code,
                value: prevProps.optionValues[code] || "",
            };
        });

        if (
            !this.props.isPlpPanelStyle &&
            this.props.rootProduct.id !== prevProps.rootProduct.id
        ) {
            let optionCodeChanges: IOptionCode[] = [];
            Array.from(preOptionValues, (option, i) => {
                if (option.value !== optionValues[i].value) {
                    optionCodeChanges = [...optionCodeChanges, option.code];
                    if (preOptionValues.length === i + 1) {
                        const options = optionCodeChanges.map((o) => {
                            // TODO(i18n): Translating string fragments separately breaks sentence structure for non-English languages. Move "option_" prefix stripping to before translation and use full sentence with interpolation
                            return o.replace("option_", " ");
                        });
                        // TODO(i18n): Using join(" and") assumes English word order and hardcodes "and" connector. Some languages need different conjunctions or word order. Use ttag list formatting or include full phrase in translation string
                        const errorMessage = interpolate(
                            t`Please <strong>confirm mattress %s</strong> before adding to cart.`,
                            [options.join(" and")],
                        );
                        this.props.dispatchers.setSetOptionSelectionErrorState(
                            errorMessage,
                        );
                    }
                }
            });
        }
    }

    private buildQuantitySelector() {
        if (
            this.props.optionSet.layout.design === ConfiguratorTypes.PANEL ||
            this.props.optionSet.layout.design === ConfiguratorTypes.UPDATED ||
            this.props.disableQuantity
        ) {
            return null;
        }
        return <QuantitySelector />;
    }

    private buildOptionSelectors() {
        return this.props.optionCodes.map((code) => (
            <ProductOptionSelectorSet
                key={code}
                selectorID={code}
                rootProduct={this.props.rootProduct}
                variant={this.props.variant}
                code={code}
                values={this.props.optionValues}
                onOptionChange={this.props.onOptionChange}
                configuratorType={this.props.optionSet.layout.design}
                style={this.props.style}
                chatLinkComponent={this.props.chatLinkComponent}
                liveChatHeader={this.props.liveChatHeader}
                enableChat={this.props.optionSet.layout.enable_chat}
                isPlpPanelStyle={this.props.isPlpPanelStyle}
            />
        ));
    }

    render() {
        const configuratorBaseClasses = classNames({
            configurator__options: true,
            [`configurator__options--${this.props.optionSet.layout.design}`]:
                this.props.optionSet.layout.design,
        });
        return (
            <div className={configuratorBaseClasses}>
                <RootProductSelector
                    optionSet={this.props.optionSet}
                    chatLinkComponent={this.props.chatLinkComponent}
                    liveChatHeader={this.props.liveChatHeader}
                    financingLink={this.props.financingLink}
                    isPlpPanelStyle={this.props.isPlpPanelStyle}
                />
                {this.buildOptionSelectors()}
                {this.buildQuantitySelector()}
            </div>
        );
    }
}

const mapStateToProps = (
    rootState: Pick<IRootState, "configurator" | "common">,
    ownProps: IOwnProps,
): IOwnProps & IReduxProps => {
    const state = rootState.configurator || defaults;
    return {
        rootProduct: rootProductSelector(state),
        productClass: state.entities.productClass,
        variant: baseVariantSelector(state),
        optionCodes: optionCodesSelector(state),
        optionValues: state.ui.optionValues,
        panelTriggerClicked: state.ui.panelTriggerClicked,
        ...ownProps,
    };
};

const mapDispatchToProps: TDispatchMapper<IDispatchProps> = (dispatch) => {
    const dispatchers = new Dispatchers(dispatch);
    return {
        dispatchers,
        onOptionChange: (namespace, code, index, totalNumValues, value) => {
            dispatchers.setOptionValue(
                namespace,
                code,
                index,
                totalNumValues,
                value,
            );
        },
    };
};

export const ConfiguratorOptions = connect(
    mapStateToProps,
    mapDispatchToProps,
)(ConfiguratorOptionsComponent);
